import { Head, Link, router } from '@inertiajs/react'
import { useEffect, useState } from 'react'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import Layout from '../../components/Layout'

interface AgendaCard {
  id: number
  name: string
  meeting_type: string
  is_required: boolean
  section_count: number
  total_duration_minutes: number
  edit_path: string
}

interface TeamData {
  id: number
  name: string
  description: string | null
  member_count: number
}

interface Props {
  team: TeamData
  active_agendas: AgendaCard[]
  can_manage_agendas: boolean
  can_start_meeting: boolean
  active_tab: string
}

const MEETING_TYPE_LABELS: Record<string, string> = {
  weekly_tactical: 'Weekly Tactical',
  monthly_strategic: 'Monthly Strategic',
  monthly_financial: 'Monthly Financial',
  rock_review: 'Rock Review',
  monthly_financial_and_rock_review: 'Financial Rock & Accountability',
  annual_planning: 'Annual Planning',
  custom: 'Custom',
}

function formatDuration(minutes: number) {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h > 0 && m > 0) return `${h}h ${m}m`
  if (h > 0) return `${h}h`
  return `${m} min`
}

export default function Agendas({ team, active_agendas, can_manage_agendas, can_start_meeting, active_tab }: Props) {
  const [agendas, setAgendas] = useState<AgendaCard[]>(active_agendas)
  const [reorderError, setReorderError] = useState<string | null>(null)

  useEffect(() => {
    setAgendas(active_agendas)
  }, [active_agendas])

  const handleStartMeeting = (agendaId: number) => {
    router.post(`/teams/${team.id}/start_meeting`, { agenda_template_id: agendaId })
  }

  const handleDragEnd = (result: any) => {
    if (!result.destination || result.destination.index === result.source.index) return
    const previous = agendas
    const next = Array.from(agendas)
    const [moved] = next.splice(result.source.index, 1)
    next.splice(result.destination.index, 0, moved)
    setAgendas(next)
    setReorderError(null)
    router.put(
      `/teams/${team.id}/agendas/reorder`,
      { agenda_template_ids: next.map(a => a.id) },
      {
        preserveScroll: true,
        preserveState: true,
        onError: () => {
          setAgendas(previous)
          setReorderError('Could not save the new order. Please try again.')
        }
      }
    )
  }

  return (
    <Layout team={team} activeTab={active_tab}>
      <Head title={`Agendas — ${team.name}`} />
      <div className="max-w-screen-2xl mx-auto px-6 py-6">

        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold text-text-primary">Agendas</h1>
          {can_manage_agendas && (
            <Link
              href={`/agenda_templates/new?team_id=${team.id}`}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-text-primary border border-charcoal rounded hover:bg-charcoal hover:text-white transition-colors"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              New Agenda
            </Link>
          )}
        </div>

        {reorderError && (
          <div className="mb-4 px-4 py-3 bg-[rgba(237,28,36,0.15)] border border-[#ED1C24]/20 rounded text-sm text-[#ED1C24]" role="alert">
            {reorderError}
          </div>
        )}

        {agendas.length === 0 ? (
          <div className="bg-white rounded-lg border border-stone/20 p-12 text-center mb-8">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mx-auto text-text-primary/20 mb-3">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
              <polyline points="10 9 9 9 8 9"/>
            </svg>
            <p className="text-text-muted text-sm">No agendas assigned to this team</p>
            {can_manage_agendas && (
              <Link
                href={`/agenda_templates/new?team_id=${team.id}`}
                className="mt-3 inline-block text-sm text-text-primary hover:underline"
              >
                Create an agenda for this team
              </Link>
            )}
          </div>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="team-agendas" direction="vertical">
              {(dropProvided) => (
                <div
                  {...dropProvided.droppableProps}
                  ref={dropProvided.innerRef}
                  className="flex flex-col gap-4 mb-8 max-w-xl"
                >
                  {agendas.map((agenda, index) => (
                    <Draggable
                      key={agenda.id}
                      draggableId={String(agenda.id)}
                      index={index}
                      isDragDisabled={!can_manage_agendas}
                    >
                      {(dragProvided, snapshot) => (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          {...dragProvided.dragHandleProps}
                          className={`bg-white rounded-lg border border-stone/20 p-5 flex flex-col ${snapshot.isDragging ? 'shadow-lg' : ''}`}
                        >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-text-primary leading-tight">{agenda.name}</h3>
                      {agenda.is_required && (
                        <span
                          className="text-xs font-semibold uppercase tracking-wide text-text-primary bg-marigold/30 border border-marigold/50 px-2 py-0.5 rounded-full flex-shrink-0"
                          title="This template is required and cannot be deleted"
                        >
                          Required
                        </span>
                      )}
                    </div>
                    <span className="text-sm text-text-muted mt-0.5 block">
                      {MEETING_TYPE_LABELS[agenda.meeting_type] || agenda.meeting_type}
                    </span>
                  </div>
                  {can_manage_agendas && (
                    <Link
                      href={agenda.edit_path}
                      className="opacity-40 hover:opacity-70 transition-opacity flex-shrink-0 ml-2"
                      aria-label="Edit agenda"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                    </Link>
                  )}
                </div>
                <div className="flex items-center gap-3 text-sm text-text-secondary mb-4">
                  <span>{agenda.section_count} section{agenda.section_count !== 1 ? 's' : ''}</span>
                  <span className="text-text-muted">·</span>
                  <span>{formatDuration(agenda.total_duration_minutes)}</span>
                </div>
                {can_start_meeting && <div className="mt-auto">
                  <button
                    onClick={() => handleStartMeeting(agenda.id)}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-pinnacle-red rounded-lg hover:bg-pinnacle-red/90 transition-colors"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="5 3 19 12 5 21 5 3"/>
                    </svg>
                    {agenda.name ? `Start ${agenda.name}` : 'Start Meeting'}
                  </button>
                          </div>}
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {dropProvided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>
    </Layout>
  )
}
